const KYC_URL = "/pending-kyc";
const TROVE_NEXT_URL = "/trove-next";

const toQueryString = (query) => {
    if (!query) return "";
    const entries = Object.entries(query).filter(([, v]) => v !== undefined && v !== null && `${v}`.trim() !== "");
    if (entries.length === 0) return "";
    return new URLSearchParams(entries).toString();
};

export const pendingKYCEndpoints = (builder, getApi) => ({
    // READ
    getAllPendingKycUserGroups: builder.query({
        query: () => ({url: `${KYC_URL}/user-group`, method: 'get'}),
        providesTags: ['Pending KYC'],
    }),
    // READ
    getUserPendingKyc: builder.query({
        query: ({userGroup, userId}) => ({url: `${KYC_URL}/user/${userGroup}/${userId}`, method: 'get'}),
        providesTags: ['User Pending KYC'],
    }),
    // UPDATE => PATCH: update the user KYC on the server
    updateUserPendingKyc: builder.mutation({
        query: ({userGroup, userId, ...userData}) => ({
            url: `${KYC_URL}/user/${userGroup}/${userId}`,
            method: 'patch',
            body: userData
        }),
        async onQueryStarted({userGroup, userId}, {dispatch, queryFulfilled}) {
            try {
                const {data: {basicDetails, ...updatedData}} = await queryFulfilled
                dispatch(
                    getApi().util.updateQueryData('getUserPendingKyc', {userGroup, userId}, (draft) => {
                        // Spread basicDetails inside updatedData
                        if (basicDetails) {
                            Object.assign(updatedData, basicDetails)
                        }
                        Object.assign(draft, updatedData)
                    })
                )
            } catch {
            }
        },
    }),
    // UPDATE => PUT: update the user risk profile on the server
    updateUserRiskProfile: builder.mutation({
        query: ({userGroup, userId, ...riskData}) => ({
            url: `${KYC_URL}/user/${userGroup}/${userId}/risk`,
            method: 'put',
            body: riskData
        }),
        async onQueryStarted({userGroup, userId}, {dispatch, queryFulfilled}) {
            try {
                const {data: updatedData} = await queryFulfilled
                dispatch(
                    getApi().util.updateQueryData('getUserPendingKyc', {userGroup, userId}, (draft) => {
                        Object.assign(draft, updatedData)
                    })
                )
            } catch {
            }
        },
    }),
    // CHECK => POST
    initiateCipCheck: builder.mutation({
        query: ({userGroup, userId, reportType}) => ({
            url: `${KYC_URL}/user/${userGroup}/${userId}/cip`,
            method: 'post',
            body: {reportType}
        }),
        invalidatesTags: ['Pending KYC'],
    }),
    // APPROVE CHECKS => POST
    approveCipChecks: builder.mutation({
        query: ({userGroup, userId, checks, reason}) => ({
            url: `${KYC_URL}/user/${userGroup}/${userId}/cip`,
            method: 'patch',
            body: {checks, reason}
        }),
        invalidatesTags: ['Pending KYC'],
    }),
    // CONFIRM => POST
    confirmKyc: builder.mutation({
        query: ({userGroup, userId, statusComment, meta}) => ({
            url: `${KYC_URL}/user/${userGroup}/${userId}`,
            method: 'post',
            body: {statusComment, meta}
        }),
        invalidatesTags: ['Pending KYC', 'User Pending KYC'],
    }),
    // DECLINE => POST
    declineKyc: builder.mutation({
        query: ({userGroup, userId, statusComment, meta}) => ({
            url: `${KYC_URL}/user/${userGroup}/${userId}/status`,
            method: 'delete',
            body: {statusComment, meta}
        }),
        invalidatesTags: ['Pending KYC', 'User Pending KYC'],
    }),
    // DECLINE => POST
    rejectKyc: builder.mutation({
        query: ({userGroup, userId, statusComment, meta}) => ({
            url: `${KYC_URL}/user/${userGroup}/${userId}`,
            method: 'delete',
            body: {statusComment, meta}
        }),
        invalidatesTags: ['Pending KYC', 'User Pending KYC'],
    }),
    // MOVE => POST
    moveToAssessed: builder.mutation({
        query: (userId) => ({
            url: `${KYC_URL}/action/${userId}/assess`,
            method: 'post'
        }),
        invalidatesTags: ['Pending KYC'],
    }),
    // MOVE => DELETE
    moveToPending: builder.mutation({
        query: (userId) => ({
            url: `${KYC_URL}/action/${userId}/assess`,
            method: 'delete'
        }),
        invalidatesTags: ['Pending KYC', 'User Pending KYC'],
    }),
    // APPROVE => POST
    approveDocument: builder.mutation({
        query: (docData) => ({url: `${KYC_URL}/document`, method: 'post', body: docData}),
        async onQueryStarted({userGroup, userId}, {dispatch, queryFulfilled}) {
            try {
                const {data: updatedDocument} = await queryFulfilled
                dispatch(
                    getApi().util.updateQueryData('getUserPendingKyc', {userGroup, userId}, (draft) => {
                        const oldDocument = draft?.docs.find((doc) => {
                            return doc.documentId === updatedDocument.documentId
                        })
                        Object.assign(oldDocument, updatedDocument)
                    })
                )
            } catch {
            }
        },
    }),
    // READ
    getDocuments: builder.query({
        query: ({userGroup, userId}) => ({url: `${KYC_URL}/documents/${userGroup}/${userId}`, method: 'get'}),
        providesTags: ['Pending KYC'],
    }),
    // READ
    getDocument: builder.query({
        query: ({userGroup, userId, documentId}) => ({url: `${KYC_URL}/document/${userGroup}/${userId}/${documentId}`, method: 'get'}),
        providesTags: ['Pending KYC'],
    }),
    // Update => PATCH
    updateDocumentFields: builder.mutation({
        query: (docData) => ({url: `${KYC_URL}/document`, method: 'patch', body: docData}),
        async onQueryStarted({userGroup, userId}, {dispatch, queryFulfilled}) {
            try {
                const {data} = await queryFulfilled
                dispatch(
                    getApi().util.updateQueryData('getUserPendingKyc', {userGroup, userId}, (draft) => {
                        const updatedDocument = {...data}
                        const oldDocument = draft?.docs.find((doc) => {
                            return doc.documentId === updatedDocument.documentId
                        })
                        delete updatedDocument.meta
                        Object.assign(oldDocument, updatedDocument)
                    })
                )
            } catch {
            }
        },
    }),
    // Update => PUT
    rejectDocument: builder.mutation({
        query: (docData) => ({url: `${KYC_URL}/document`, method: 'put', body: docData}),
        invalidatesTags: ['Pending KYC', 'User Pending KYC', 'Corporate Accounts'],
        async onQueryStarted({userGroup, userId}, {dispatch, queryFulfilled}) {
            try {
                const {data: updatedDocument} = await queryFulfilled
                dispatch(
                    getApi().util.updateQueryData('getUserPendingKyc', {userGroup, userId}, (draft) => {
                        const oldDocument = draft?.docs.find((doc) => {
                            return doc.documentId === updatedDocument.documentId
                        })
                        Object.assign(oldDocument, updatedDocument)
                    })
                )
            } catch {
            }
        },
    }),
    // RESET => POST
    resetDocument: builder.mutation({
        query: (docData) => ({url: `${KYC_URL}/document/reset`, method: 'post', body: docData}),
        async onQueryStarted({userGroup, userId}, {dispatch, queryFulfilled}) {
            try {
                const {data: updatedDocument} = await queryFulfilled
                dispatch(
                    getApi().util.updateQueryData('getUserPendingKyc', {userGroup, userId}, (draft) => {
                        const oldDocument = draft?.docs.find((doc) => {
                            return doc.documentId === updatedDocument.documentId
                        })
                        Object.assign(oldDocument, updatedDocument)
                    })
                )
            } catch {
            }
        },
    }),
    // APPROVE ADDRESS UPDATE => POST
    approveAddressUpdate: builder.mutation({
        query: (body) => ({
            url: `/address-update/approve`,
            method: 'patch',
            body
        }),
        // Note: the approve payload only carries {updateAddressId, reviewedBy} (see
        // ApproveAddressUpdate.patch() on the backend) — no userId — so this can't be
        // scoped to a single cached entry by id. Invalidating the flat tag is correct
        // and cheap here: there's only ever one Address Update query active at a time
        // (the tab in the open KYC modal), so this just makes that one refetch.
        invalidatesTags: ['User Pending KYC', 'Address Update Requests'],
    }),
    // REJECT ADDRESS UPDATE => POST
    rejectAddressUpdate: builder.mutation({
        query: (body) => ({
            url: `/address-update/reject`,
            method: 'patch',
            body
        }),
        invalidatesTags: ['User Pending KYC', 'Address Update Requests'],
    }),
    // READ ADDRESS UPDATE REQUESTS
    getAddressUpdateRequests: builder.query({
        query: ({ userId, status }) => ({
            url: `/address-update?userId=${userId}${status ? `&status=${status}` : ''}`,
            method: 'get'
        }),
        providesTags: ['Address Update Requests'],
    }),
    // GENERATE REPORT => POST
    downloadKYCReport: builder.mutation({
        query: ({ userGroup, startDate, endDate, format }) => ({
            url: `${KYC_URL}/report/${format}?userGroup=${userGroup}`
                + `&startDate=${startDate || ""}`
                + `&endDate=${endDate || ""}`,
            method: 'post'
        }),
    }),

    getTroveNextProfiles: builder.query({
        query: ({ query } = {}) => {
            const qs = toQueryString(query);
            return { url: `${TROVE_NEXT_URL}/profiles${qs ? `?${qs}` : ""}`, method: "get" };
        },
        providesTags: ["Trove Next"],
    }),

    getTroveNextProfileDocuments: builder.query({
        query: ({ profileId, query } = {}) => {
            const qs = toQueryString(query);
            return { url: `${TROVE_NEXT_URL}/profiles/documents/${profileId}${qs ? `?${qs}` : ""}`, method: "get" };
        },
        providesTags: ["Trove Next"],
    }),


    approveTroveNextProfileDocument: builder.mutation({
        query: ({ documentId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${TROVE_NEXT_URL}/documents/${documentId}/approve${qs ? `?${qs}` : ""}`,
                method: "post",
                body: body ?? null,
            };
        },
        invalidatesTags: ["Trove Next"],
    }),

    rejectTroveNextProfileDocument: builder.mutation({
        query: ({ documentId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${TROVE_NEXT_URL}/documents/${documentId}/reject${qs ? `?${qs}` : ""}`,
                method: "post",
                body: body ?? null,
            };
        },
        invalidatesTags: ["Trove Next"],
    }),

    resetTroveNextProfileDocument: builder.mutation({
        query: ({ documentId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${TROVE_NEXT_URL}/documents/${documentId}/reset${qs ? `?${qs}` : ""}`,
                method: "post",
                body: body ?? null,
            };
        },
        invalidatesTags: ["Trove Next"],
    }),

    getTroveNextProfileTransitionStatus: builder.query({
        query: ({ profileId, query } = {}) => {
            const qs = toQueryString(query);
            return { url: `${TROVE_NEXT_URL}/profiles/transition/status/${profileId}${qs ? `?${qs}` : ""}`, method: "get" };
        },
        providesTags: ["Trove Next"],
    }),

    activateTroveNextProfile: builder.mutation({
        query: ({ profileId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${TROVE_NEXT_URL}/profiles/activate/${profileId}${qs ? `?${qs}` : ""}`,
                method: "post",
                body: body ?? null,
            };
        },
        invalidatesTags: ["Trove Next"],
    }),

    approveTroveNextProfileTransition: builder.mutation({
        query: ({ profileId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${TROVE_NEXT_URL}/profiles/transition/approve/${profileId}${qs ? `?${qs}` : ""}`,
                method: "post",
                body: body ?? null,
            };
        },
        invalidatesTags: ["Trove Next"],
    }),

    updateTroveNextProfile: builder.mutation({
        query: ({ profileId, query, body } = {}) => {
            const qs = toQueryString(query);
            return {
                url: `${TROVE_NEXT_URL}/profiles/${profileId}${qs ? `?${qs}` : ""}`,
                method: "put",
                body: body ?? null,
            };
        },
        invalidatesTags: ["Trove Next"],
    }),
})
